import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  MoreVertical, 
  Plus,
  ArrowRight,
  Settings,
  Eye,
  EyeOff,
  Filter,
  Search,
  Bell,
  Download,
  Share2,
  BarChart2,
  PieChart,
  TrendingUp,
  CreditCard,
  RefreshCw,
  Home,
  Car,
  Utensils,
  ShoppingBag,
  Heart,
  Wifi,
  Music,
  Play
} from 'lucide-react';

// Mock brand logos - in a real app these would be imported from your assets
const BrandLogos = {
  Netflix: 'https://upload.wikimedia.org/wikipedia/commons/7/77/Netflix_2015_logo.svg',
  Spotify: 'https://upload.wikimedia.org/wikipedia/commons/1/19/Spotify_logo_without_text.svg',
  Amazon: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg',
  Uber: 'https://upload.wikimedia.org/wikipedia/commons/5/58/Uber_logo_2018.svg',
  Starbucks: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/d3/Starbucks_Corporation_Logo_2011.svg/1200px-Starbucks_Corporation_Logo_2011.svg.png',
  PlayStation: 'https://upload.wikimedia.org/wikipedia/commons/0/00/PlayStation_logo.svg',
  Tesla: 'https://upload.wikimedia.org/wikipedia/commons/b/bd/Tesla_Motors.svg',
  TMobile: 'https://upload.wikimedia.org/wikipedia/commons/8/84/T-Mobile_Logo_2023.svg',
  Audible: 'https://upload.wikimedia.org/wikipedia/commons/7/73/Audible_logo.svg',
  MyFitnessPal: 'https://upload.wikimedia.org/wikipedia/commons/7/7e/MyFitnessPal_Logo.png'
};

const FinancialDashboard = ({ darkMode }) => {
 
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [showBalances, setShowBalances] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedCard, setExpandedCard] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState(3);
  const [isLoading, setIsLoading] = useState(false);

  // Theme configuration
  const theme = {
    dark: {
      bg: 'bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900/20',
      card: 'bg-gray-800/80 backdrop-blur-md border border-gray-700/50',
      highlight: 'bg-gray-700/60 border border-gray-600/40',
      text: 'text-white',
      textSecondary: 'text-gray-400',
      accent: 'text-blue-400',
      input: 'bg-gray-800/60 border-gray-700/60 text-white placeholder-gray-500',
      icon: 'text-gray-300',
      divider: 'border-gray-700'
    },
    light: {
      bg: 'bg-gradient-to-br from-blue-50 via-emerald-50 to-white',
      card: 'bg-white/90 backdrop-blur-md border border-gray-200/60',
      highlight: 'bg-white border border-gray-200/70',
      text: 'text-gray-900',
      textSecondary: 'text-gray-600',
      accent: 'text-blue-600',
      input: 'bg-white/80 border-gray-300 text-gray-900 placeholder-gray-400',
      icon: 'text-gray-600',
      divider: 'border-gray-200'
    }
  };

  const currentTheme = darkMode ? theme.dark : theme.light;

  // Mock data with enhanced structure
  const financialData = {
    totalBalance: 12543.27,
    monthlySpending: 2845.60,
    savingsRate: 18.7,
    connectedAccounts: 8,
    creditScore: 782,
    upcomingBills: [
      { 
        name: 'Netflix', 
        amount: 15.99, 
        due: 'Tomorrow', 
        logo: BrandLogos.Netflix,
        category: 'Entertainment',
        icon: Play
      },
      { 
        name: 'Car Payment', 
        amount: 389.00, 
        due: 'May 5', 
        logo: null,
        category: 'Transport',
        icon: Car
      },
      { 
        name: 'Mortgage', 
        amount: 1450.00, 
        due: 'May 15', 
        logo: null,
        category: 'Housing',
        icon: Home
      }
    ],
    spendingCategories: [
      { name: 'Housing', amount: 1450.00, percentage: 51, color: 'bg-blue-500', icon: Home },
      { name: 'Transport', amount: 420.50, percentage: 15, color: 'bg-yellow-500', icon: Car },
      { name: 'Food', amount: 387.25, percentage: 14, color: 'bg-green-500', icon: Utensils },
      { name: 'Entertainment', amount: 215.75, percentage: 8, color: 'bg-purple-500', icon: Play },
      { name: 'Utilities', amount: 185.10, percentage: 7, color: 'bg-gray-500', icon: Wifi },
      { name: 'Other', amount: 187.00, percentage: 5, color: 'bg-pink-500', icon: ShoppingBag }
    ],
    subscriptions: [
      { 
        id: 1,
        name: 'Netflix', 
        amount: 15.99, 
        category: 'Entertainment', 
        status: 'active', 
        logo: BrandLogos.Netflix,
        nextCharge: 'May 15, 2023',
        usage: '12h 45m this month'
      },
      { 
        id: 2,
        name: 'Spotify Premium', 
        amount: 9.99, 
        category: 'Entertainment', 
        status: 'active', 
        logo: BrandLogos.Spotify,
        nextCharge: 'May 20, 2023',
        usage: 'Daily user'
      },
      { 
        id: 3,
        name: 'Amazon Prime', 
        amount: 12.99, 
        category: 'Shopping', 
        status: 'active', 
        logo: BrandLogos.Amazon,
        nextCharge: 'Jun 1, 2023',
        usage: '3 orders this month'
      },
      { 
        id: 4,
        name: 'Uber One', 
        amount: 9.99, 
        category: 'Transport', 
        status: 'active', 
        logo: BrandLogos.Uber,
        nextCharge: 'May 18, 2023',
        usage: '5 rides this month'
      },
      { 
        id: 5,
        name: 'PlayStation Plus', 
        amount: 9.99, 
        category: 'Gaming', 
        status: 'active', 
        logo: BrandLogos.PlayStation,
        nextCharge: 'Jun 5, 2023',
        usage: '8h gaming this week'
      },
      { 
        id: 6,
        name: 'MyFitnessPal Premium', 
        amount: 9.99, 
        category: 'Health', 
        status: 'active', 
        logo: BrandLogos.MyFitnessPal,
        nextCharge: 'May 25, 2023',
        usage: 'Daily user'
      },
      { 
        id: 7,
        name: 'Audible', 
        amount: 14.95, 
        category: 'Education', 
        status: 'paused', 
        logo: BrandLogos.Audible,
        nextCharge: 'Paused',
        usage: '1 book last month'
      },
      { 
        id: 8,
        name: 'T-Mobile', 
        amount: 85.00, 
        category: 'Utilities', 
        status: 'active', 
        logo: BrandLogos.TMobile,
        nextCharge: 'May 30, 2023',
        usage: 'Unlimited data'
      }
    ],
    recentTransactions: [
      { 
        id: 1,
        name: 'Starbucks', 
        amount: 5.75, 
        date: 'Today', 
        category: 'Food', 
        logo: BrandLogos.Starbucks,
        location: 'New York, NY',
        paymentMethod: 'Visa ****4242'
      },
      { 
        id: 2,
        name: 'Tesla Supercharger', 
        amount: 15.60, 
        date: 'Today', 
        category: 'Transport', 
        logo: BrandLogos.Tesla,
        location: 'Boston, MA',
        paymentMethod: 'Visa ****4242'
      },
      { 
        id: 3,
        name: 'Amazon Purchase', 
        amount: 89.99, 
        date: 'Yesterday', 
        category: 'Shopping', 
        logo: BrandLogos.Amazon,
        location: 'Online',
        paymentMethod: 'Amex ****1001'
      },
      { 
        id: 4,
        name: 'Spotify Subscription', 
        amount: 9.99, 
        date: '2 days ago', 
        category: 'Entertainment', 
        logo: BrandLogos.Spotify,
        location: 'Auto-pay',
        paymentMethod: 'Visa ****4242'
      },
      { 
        id: 5,
        name: 'Netflix Subscription', 
        amount: 15.99, 
        date: '5 days ago', 
        category: 'Entertainment', 
        logo: BrandLogos.Netflix,
        location: 'Auto-pay',
        paymentMethod: 'Visa ****4242'
      }
    ],
    investmentAccounts: [
      { 
        id: 1,
        name: '401(k)', 
        balance: 45600.00, 
        change: 3.2, 
        type: 'Retirement',
        allocation: [
          { name: 'Stocks', percentage: 65 },
          { name: 'Bonds', percentage: 25 },
          { name: 'Cash', percentage: 10 }
        ],
        performance: [3.2, 1.8, 4.5, -0.5, 2.1]
      },
      { 
        id: 2,
        name: 'Roth IRA', 
        balance: 18750.00, 
        change: 1.8, 
        type: 'Retirement',
        allocation: [
          { name: 'Stocks', percentage: 80 },
          { name: 'Bonds', percentage: 15 },
          { name: 'Cash', percentage: 5 }
        ],
        performance: [2.8, 1.2, 3.9, -1.2, 1.8]
      },
      { 
        id: 3,
        name: 'Brokerage', 
        balance: 32500.00, 
        change: -0.5, 
        type: 'Investment',
        allocation: [
          { name: 'Tech', percentage: 45 },
          { name: 'Healthcare', percentage: 25 },
          { name: 'Energy', percentage: 15 },
          { name: 'Other', percentage: 15 }
        ],
        performance: [5.2, -2.1, 1.5, -3.2, -0.5]
      }
    ]
  };

  // Filter subscriptions based on search
  const filteredSubscriptions = financialData.subscriptions.filter(sub => 
    sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sub.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Simulate loading state
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, [activeTab]);

  // Handle tab change with loading state
  const handleTabChange = (tab) => {
    setIsLoading(true);
    setActiveTab(tab);
    setTimeout(() => setIsLoading(false), 800);
  };

  

  return (
    <div className={`min-h-screen ${currentTheme.bg} p-4 md:p-6 transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto">
        {/* Header with user controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
          <div className="flex items-center space-x-3">
            <CreditCard className={`w-7 h-7 md:w-8 md:h-8 ${currentTheme.accent}`} />
            <h1 className={`text-2xl md:text-3xl font-light ${currentTheme.text}`}>Financial Dashboard</h1>
            <div className={`px-2 py-1 rounded-full text-xs ${currentTheme.highlight} ${currentTheme.textSecondary}`}>
              {financialData.connectedAccounts} accounts connected
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button 
              className={`p-2 rounded-xl ${currentTheme.highlight} relative hover:scale-105 transition-all`}
              onClick={() => setNotifications(0)}
            >
              <Bell className={`w-5 h-5 ${currentTheme.icon}`} />
              {notifications > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {notifications}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowBalances(!showBalances)}
              className={`p-2 rounded-xl ${currentTheme.highlight} hover:scale-105 transition-all`}
            >
              {showBalances ? (
                <Eye className={`w-5 h-5 ${currentTheme.icon}`} />
              ) : (
                <EyeOff className={`w-5 h-5 ${currentTheme.icon}`} />
              )}
            </button>
          
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className={`p-1 rounded-2xl ${currentTheme.highlight} mb-6 md:mb-8 flex overflow-x-auto`}>
          {['overview', 'accounts', 'spending', 'investments', 'subscriptions'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-4 py-2 md:px-6 md:py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab 
                  ? 'bg-blue-500 text-white shadow-lg' 
                  : `${currentTheme.textSecondary} hover:${currentTheme.text}`
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Main Content */}
        {!isLoading && (
          <>
            {/* Overview Dashboard */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Financial Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                  <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border hover:shadow-xl transition-all`}>
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <h3 className={`text-sm ${currentTheme.textSecondary}`}>Total Balance</h3>
                      <TrendingUp className={`w-5 h-5 text-green-400`} />
                    </div>
                    <div className={`text-2xl md:text-3xl font-light mb-2 ${currentTheme.text}`}>
                      {showBalances ? `$${financialData.totalBalance.toLocaleString()}` : '••••••'}
                    </div>
                    <div className="text-sm text-green-400">+2.4% from last month</div>
                  </div>

                  <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border hover:shadow-xl transition-all`}>
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <h3 className={`text-sm ${currentTheme.textSecondary}`}>Monthly Spending</h3>
                      <BarChart2 className={`w-5 h-5 text-blue-400`} />
                    </div>
                    <div className={`text-2xl md:text-3xl font-light mb-2 ${currentTheme.text}`}>
                      {showBalances ? `$${financialData.monthlySpending.toLocaleString()}` : '••••••'}
                    </div>
                    <div className="text-sm text-blue-400">On track with budget</div>
                  </div>

                  <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border hover:shadow-xl transition-all`}>
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <h3 className={`text-sm ${currentTheme.textSecondary}`}>Savings Rate</h3>
                      <PieChart className={`w-5 h-5 text-purple-400`} />
                    </div>
                    <div className={`text-2xl md:text-3xl font-light mb-2 ${currentTheme.text}`}>
                      {showBalances ? `${financialData.savingsRate}%` : '••%'}
                    </div>
                    <div className="text-sm text-purple-400">Goal: 20%</div>
                  </div>

                  <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border hover:shadow-xl transition-all`}>
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <h3 className={`text-sm ${currentTheme.textSecondary}`}>Credit Score</h3>
                      <CreditCard className={`w-5 h-5 text-yellow-400`} />
                    </div>
                    <div className={`text-2xl md:text-3xl font-light mb-2 ${currentTheme.text}`}>
                      {showBalances ? financialData.creditScore : '•••'}
                    </div>
                    <div className="text-sm text-yellow-400">Excellent</div>
                  </div>
                </div>

                {/* Spending Breakdown and Recent Transactions */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                  {/* Spending Breakdown */}
                  <div className={`lg:col-span-2 p-4 md:p-6 rounded-2xl ${currentTheme.card} border`}>
                    <div className="flex items-center justify-between mb-4 md:mb-6">
                      <h3 className={`text-lg md:text-xl font-light ${currentTheme.text}`}>Spending Breakdown</h3>
                      <select 
                        className={`px-2 py-1 md:px-3 md:py-2 rounded-xl ${currentTheme.input} text-sm border outline-none`}
                        value={selectedPeriod}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                      >
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="year">This Year</option>
                      </select>
                    </div>
                    
                    <div className="space-y-3 md:space-y-4">
                      {financialData.spendingCategories.map((category, index) => (
                        <div key={index} className="flex items-center">
                          <div className="w-20 md:w-24 flex items-center">
                            <category.icon className={`w-4 h-4 mr-2 ${currentTheme.icon}`} />
                            <span className={`text-sm ${currentTheme.text}`}>{category.name}</span>
                          </div>
                          <div className="flex-1 mx-2 md:mx-4">
                            <div className="h-2 md:h-3 bg-gray-500/20 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${category.color}`}
                                style={{ width: `${category.percentage}%` }}
                              />
                            </div>
                          </div>
                          <div className="w-16 md:w-24 text-right">
                            <span className={`text-xs md:text-sm font-medium ${currentTheme.text}`}>
                              {showBalances ? `$${category.amount}` : '••••'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Upcoming Bills */}
                  <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border`}>
                    <div className="flex items-center justify-between mb-4 md:mb-6">
                      <h3 className={`text-lg md:text-xl font-light ${currentTheme.text}`}>Upcoming Bills</h3>
                      <button className={`text-xs md:text-sm ${currentTheme.accent} hover:underline`}>
                        View All
                      </button>
                    </div>
                    
                    <div className="space-y-3 md:space-y-4">
                      {financialData.upcomingBills.map((bill, index) => (
                        <div key={index} className="flex items-center justify-between p-2 md:p-3 hover:bg-gray-700/20 rounded-xl transition-colors">
                          <div className="flex items-center space-x-2 md:space-x-3">
                            {bill.logo ? (
                              <img 
                                src={bill.logo} 
                                alt={bill.name} 
                                className="w-8 h-8 md:w-10 md:h-10 rounded-lg object-contain bg-white p-1"
                              />
                            ) : (
                              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center ${currentTheme.highlight}`}>
                                <bill.icon className={`w-4 h-4 md:w-5 md:h-5 ${currentTheme.icon}`} />
                              </div>
                            )}
                            <div>
                              <div className={`text-xs md:text-sm font-medium ${currentTheme.text}`}>{bill.name}</div>
                              <div className={`text-xs ${currentTheme.textSecondary}`}>Due {bill.due}</div>
                            </div>
                          </div>
                          <div className={`text-xs md:text-sm font-medium ${currentTheme.text}`}>
                            {showBalances ? `$${bill.amount}` : '•••'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recent Transactions and Investment Accounts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                  {/* Recent Transactions */}
                  <div className={`lg:col-span-2 p-4 md:p-6 rounded-2xl ${currentTheme.card} border`}>
                    <div className="flex items-center justify-between mb-4 md:mb-6">
                      <h3 className={`text-lg md:text-xl font-light ${currentTheme.text}`}>Recent Transactions</h3>
                      <div className="flex items-center space-x-2">
                        <button className={`p-1 md:p-2 rounded-lg ${currentTheme.highlight}`}>
                          <Filter className="w-4 h-4" />
                        </button>
                        <button className={`text-xs md:text-sm ${currentTheme.accent} hover:underline`}>
                          View All
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-3 md:space-y-4">
                      {financialData.recentTransactions.map((transaction, index) => (
                        <div key={index} className="flex items-center justify-between p-2 md:p-3 hover:bg-gray-700/20 rounded-xl transition-colors">
                          <div className="flex items-center space-x-2 md:space-x-3">
                            <img 
                              src={transaction.logo} 
                              alt={transaction.name} 
                              className="w-8 h-8 md:w-10 md:h-10 rounded-lg object-contain bg-white p-1"
                            />
                            <div>
                              <div className={`text-xs md:text-sm font-medium ${currentTheme.text}`}>{transaction.name}</div>
                              <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>
                                {transaction.date} • {transaction.category}
                              </div>
                            </div>
                          </div>
                          <div className={`text-xs md:text-sm font-medium ${currentTheme.text}`}>
                            {showBalances ? `-$${transaction.amount}` : '-•••'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Investment Accounts */}
                  <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border`}>
                    <div className="flex items-center justify-between mb-4 md:mb-6">
                      <h3 className={`text-lg md:text-xl font-light ${currentTheme.text}`}>Investments</h3>
                      <button className={`text-xs md:text-sm ${currentTheme.accent} hover:underline`}>
                        View All
                      </button>
                    </div>
                    
                    <div className="space-y-3 md:space-y-4">
                      {financialData.investmentAccounts.map((account, index) => (
                        <div 
                          key={index} 
                          className={`p-3 md:p-4 rounded-xl ${currentTheme.highlight} cursor-pointer transition-all hover:scale-[1.02]`}
                          onClick={() => setExpandedCard(expandedCard === index ? null : index)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className={`text-xs md:text-sm font-medium ${currentTheme.text}`}>{account.name}</div>
                              <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>{account.type}</div>
                            </div>
                            <div className="text-right">
                              <div className={`text-xs md:text-sm font-medium ${currentTheme.text}`}>
                                {showBalances ? `$${account.balance.toLocaleString()}` : '••••••'}
                              </div>
                              <div className={`text-2xs md:text-xs ${account.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {showBalances ? `${account.change >= 0 ? '+' : ''}${account.change}%` : '••%'}
                              </div>
                            </div>
                          </div>
                          
                          {expandedCard === index && (
                            <div className="mt-3 pt-3 border-t border-gray-700/30">
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>YTD Return</div>
                                  <div className={`text-xs md:text-sm ${currentTheme.text}`}>+8.2%</div>
                                </div>
                                <div>
                                  <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>Risk Level</div>
                                  <div className={`text-xs md:text-sm ${currentTheme.text}`}>Moderate</div>
                                </div>
                                <div>
                                  <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>Fees</div>
                                  <div className={`text-xs md:text-sm ${currentTheme.text}`}>0.12%</div>
                                </div>
                                <div>
                                  <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>Allocation</div>
                                  <div className={`text-xs md:text-sm ${currentTheme.text}`}>Diversified</div>
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <button className={`px-2 py-1 text-2xs md:text-xs rounded-lg ${currentTheme.highlight} hover:bg-gray-600/50`}>
                                  View Details
                                </button>
                                <button className={`px-2 py-1 text-2xs md:text-xs rounded-lg ${currentTheme.highlight} hover:bg-gray-600/50`}>
                                  Rebalance
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Subscriptions Management */}
            {activeTab === 'subscriptions' && (
              <div className="space-y-4 md:space-y-6">
                {/* Subscription Summary */}
                <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6 gap-4">
                    <div>
                      <h3 className={`text-lg md:text-xl font-light ${currentTheme.text}`}>Subscription Management</h3>
                      <p className={`text-xs md:text-sm ${currentTheme.textSecondary}`}>
                        {filteredSubscriptions.length} active services • ${filteredSubscriptions.reduce((sum, sub) => sum + sub.amount, 0).toFixed(2)}/month
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 md:space-x-3">
                      <div className={`relative ${currentTheme.input} rounded-xl border flex items-center px-2 md:px-3 py-1 md:py-2`}>
                        <Search className={`w-4 h-4 ${currentTheme.icon} mr-2`} />
                        <input
                          type="text"
                          placeholder="Search subscriptions..."
                          className="bg-transparent outline-none text-xs md:text-sm w-32 md:w-40"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                      <button className={`p-1 md:p-2 rounded-xl ${currentTheme.highlight}`}>
                        <Filter className="w-4 h-4" />
                      </button>
                      <button className={`p-1 md:p-2 rounded-xl ${currentTheme.highlight} hover:bg-blue-500/20`}>
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Subscription Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                    {filteredSubscriptions.map((subscription) => (
                      <div 
                        key={subscription.id} 
                        className={`p-3 md:p-4 rounded-xl ${currentTheme.highlight} border hover:shadow-lg transition-all group`}
                      >
                        <div className="flex items-start justify-between mb-3 md:mb-4">
                          <div className="flex items-center space-x-2 md:space-x-3">
                            <img 
                              src={subscription.logo} 
                              alt={subscription.name} 
                              className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-contain bg-white p-1"
                            />
                            <div>
                              <div className={`text-sm md:text-base font-medium ${currentTheme.text}`}>{subscription.name}</div>
                              <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>{subscription.category}</div>
                            </div>
                          </div>
                          <button className={`p-1 rounded-lg opacity-0 group-hover:opacity-100 ${currentTheme.highlight}`}>
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between mb-2 md:mb-3">
                          <div className={`text-lg md:text-xl font-light ${currentTheme.text}`}>
                            {showBalances ? `$${subscription.amount}` : '•••'}
                            <span className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>/month</span>
                          </div>
                          <div className={`px-2 py-1 rounded-full text-2xs md:text-xs ${
                            subscription.status === 'active' 
                              ? 'bg-green-500/20 text-green-400' 
                              : subscription.status === 'paused' 
                                ? 'bg-yellow-500/20 text-yellow-400' 
                                : 'bg-red-500/20 text-red-400'
                          }`}>
                            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                          </div>
                        </div>
                        
                        <div className={`text-2xs md:text-xs ${currentTheme.textSecondary} mb-3`}>
                          Next charge: {subscription.nextCharge}
                        </div>
                        
                        <div className="flex space-x-2 pt-2 md:pt-3 border-t border-gray-700/30">
                          <button className={`flex-1 py-1 text-2xs md:text-xs rounded-lg ${currentTheme.highlight} hover:bg-gray-600/50`}>
                            {subscription.status === 'active' ? 'Pause' : 'Resume'}
                          </button>
                          <button className={`flex-1 py-1 text-2xs md:text-xs rounded-lg ${currentTheme.highlight} hover:bg-gray-600/50`}>
                            Details
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Add New Subscription Card */}
                    <div 
                      className={`p-4 rounded-xl border-2 border-dashed ${currentTheme.textSecondary} flex flex-col items-center justify-center hover:border-blue-400 hover:text-blue-400 cursor-pointer transition-all`}
                    >
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-2 md:mb-3">
                        <Plus className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className={`text-sm md:text-base font-medium ${currentTheme.text}`}>Add Subscription</div>
                      <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>Connect a new service</div>
                    </div>
                  </div>
                </div>
                
                {/* Subscription Analytics */}
                <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border`}>
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <h3 className={`text-lg md:text-xl font-light ${currentTheme.text}`}>Subscription Analytics</h3>
                    <div className="flex items-center space-x-2">
                      <button className={`p-1 md:p-2 rounded-lg ${currentTheme.highlight}`}>
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button className={`p-1 md:p-2 rounded-lg ${currentTheme.highlight}`}>
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    <div className={`p-3 md:p-4 rounded-xl ${currentTheme.highlight} border`}>
                      <div className={`text-xs md:text-sm ${currentTheme.textSecondary} mb-1 md:mb-2`}>Monthly Cost</div>
                      <div className={`text-xl md:text-2xl font-light ${currentTheme.text} mb-2 md:mb-3`}>
                        {showBalances ? `$${financialData.subscriptions.reduce((sum, sub) => sum + sub.amount, 0).toFixed(2)}` : '•••'}
                      </div>
                      <div className="text-2xs md:text-xs text-green-400">↓ 12% from last month</div>
                    </div>
                    
                    <div className={`p-3 md:p-4 rounded-xl ${currentTheme.highlight} border`}>
                      <div className={`text-xs md:text-sm ${currentTheme.textSecondary} mb-1 md:mb-2`}>Most Expensive</div>
                      <div className="flex items-center space-x-2 md:space-x-3">
                        <img 
                          src={BrandLogos.TMobile} 
                          alt="T-Mobile" 
                          className="w-6 h-6 md:w-8 md:h-8 rounded-lg object-contain bg-white p-0.5"
                        />
                        <div>
                          <div className={`text-base md:text-lg font-light ${currentTheme.text}`}>
                            {showBalances ? '$85.00' : '•••'}
                          </div>
                          <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>T-Mobile</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-3 md:p-4 rounded-xl ${currentTheme.highlight} border`}>
                      <div className={`text-xs md:text-sm ${currentTheme.textSecondary} mb-1 md:mb-2`}>Most Used</div>
                      <div className="flex items-center space-x-2 md:space-x-3">
                        <img 
                          src={BrandLogos.Spotify} 
                          alt="Spotify" 
                          className="w-6 h-6 md:w-8 md:h-8 rounded-lg object-contain bg-white p-0.5"
                        />
                        <div>
                          <div className={`text-base md:text-lg font-light ${currentTheme.text}`}>Daily</div>
                          <div className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>Spotify</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-3 md:p-4 rounded-xl ${currentTheme.highlight} border`}>
                      <div className={`text-xs md:text-sm ${currentTheme.textSecondary} mb-1 md:mb-2`}>By Category</div>
                      <div className="space-y-1 md:space-y-2">
                        {['Entertainment', 'Utilities', 'Health', 'Shopping'].map((category, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>{category}</span>
                            <span className={`text-2xs md:text-xs font-medium ${currentTheme.text}`}>
                              {showBalances ? `$${financialData.subscriptions
                                .filter(sub => sub.category === category)
                                .reduce((sum, sub) => sum + sub.amount, 0)
                                .toFixed(2)}` : '•••'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Accounts Tab */}
            {activeTab === 'accounts' && (
              <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border`}>
                <h3 className={`text-lg md:text-xl font-light ${currentTheme.text} mb-4 md:mb-6`}>Connected Accounts</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {financialData.subscriptions.map((account) => (
                    <div key={account.id} className={`p-3 md:p-4 rounded-xl ${currentTheme.highlight} border`}>
                      <div className="flex items-center space-x-3 mb-3">
                        <img 
                          src={account.logo} 
                          alt={account.name} 
                          className="w-10 h-10 rounded-lg object-contain bg-white p-1"
                        />
                        <div>
                          <h4 className={`text-sm md:text-base font-medium ${currentTheme.text}`}>{account.name}</h4>
                          <p className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>{account.category}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-xs md:text-sm ${currentTheme.textSecondary}`}>Status</span>
                        <span className={`text-xs md:text-sm ${
                          account.status === 'active' ? 'text-green-400' : 
                          account.status === 'paused' ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className={`text-xs md:text-sm ${currentTheme.textSecondary}`}>Next Charge</span>
                        <span className={`text-xs md:text-sm ${currentTheme.text}`}>{account.nextCharge}</span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className={`text-xs md:text-sm ${currentTheme.textSecondary}`}>Amount</span>
                        <span className={`text-xs md:text-sm ${currentTheme.text}`}>
                          {showBalances ? `$${account.amount}` : '•••'}
                        </span>
                      </div>
                      <div className="mt-3 pt-2 border-t border-gray-700/30 flex space-x-2">
                        <button className={`flex-1 py-1 text-2xs md:text-xs rounded-lg ${currentTheme.highlight} hover:bg-gray-600/50`}>
                          Manage
                        </button>
                        <button className={`flex-1 py-1 text-2xs md:text-xs rounded-lg ${currentTheme.highlight} hover:bg-gray-600/50`}>
                          Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Spending Tab */}
            {activeTab === 'spending' && (
              <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border`}>
                <h3 className={`text-lg md:text-xl font-light ${currentTheme.text} mb-4 md:mb-6`}>Spending Analysis</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className={`text-sm md:text-base font-medium ${currentTheme.text} mb-3`}>Monthly Spending</h4>
                    <div className={`h-64 rounded-xl ${currentTheme.highlight} flex items-center justify-center`}>
                      <PieChart className="w-16 h-16 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <h4 className={`text-sm md:text-base font-medium ${currentTheme.text} mb-3`}>Spending Trends</h4>
                    <div className={`h-64 rounded-xl ${currentTheme.highlight} flex items-center justify-center`}>
                      <BarChart2 className="w-16 h-16 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Investments Tab */}
            {activeTab === 'investments' && (
              <div className={`p-4 md:p-6 rounded-2xl ${currentTheme.card} border`}>
                <h3 className={`text-lg md:text-xl font-light ${currentTheme.text} mb-4 md:mb-6`}>Investment Portfolio</h3>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                  {financialData.investmentAccounts.map((account) => (
                    <div key={account.id} className={`p-4 rounded-xl ${currentTheme.highlight} border`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className={`text-sm md:text-base font-medium ${currentTheme.text}`}>{account.name}</h4>
                          <p className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>{account.type}</p>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm md:text-base font-medium ${currentTheme.text}`}>
                            {showBalances ? `$${account.balance.toLocaleString()}` : '••••••'}
                          </div>
                          <div className={`text-2xs md:text-xs ${
                            account.change >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {showBalances ? `${account.change >= 0 ? '+' : ''}${account.change}%` : '••%'}
                          </div>
                        </div>
                      </div>
                      <div className="h-32 rounded-lg bg-gray-700/30 mb-3 flex items-center justify-center">
                        <TrendingUp className="w-8 h-8 text-gray-400" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-2xs md:text-xs ${currentTheme.textSecondary}`}>Allocation</span>
                        <span className={`text-2xs md:text-xs ${currentTheme.text}`}>
                          {account.allocation.map(a => a.name).join(', ')}
                        </span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-700/30 flex space-x-2">
                        <button className={`flex-1 py-1 text-2xs md:text-xs rounded-lg ${currentTheme.highlight} hover:bg-gray-600/50`}>
                          View
                        </button>
                        <button className={`flex-1 py-1 text-2xs md:text-xs rounded-lg ${currentTheme.highlight} hover:bg-gray-600/50`}>
                          Trade
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FinancialDashboard;